import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Download, Edit, Trash2, BarChart3, Calendar, User, Eye, FileText } from 'lucide-react';
import { formsAPI } from '../../lib/api.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate } from '../../utils/formatters.js';
import { hasCapability, isAdmin } from '../../lib/rbac.js';

function FormDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showStats, setShowStats] = useState(false);

  const { data: form, isLoading } = useQuery({
    queryKey: ['form', id],
    queryFn: () => formsAPI.getById(id),
  });

  const { data: stats } = useQuery({
    queryKey: ['form-stats', id],
    queryFn: () => formsAPI.getStats(id),
    enabled: isAdmin() && showStats,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => formsAPI.delete(id),
    onSuccess: () => {
      navigate('/forms');
    },
  });

  const handleDownload = async () => {
    try {
      const response = await formsAPI.download(id);
      const { url } = response.data;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this form?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = () => {
    navigate(`/forms/${id}/edit`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!form?.data) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Form not found</h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2">The form you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/forms')} className="mt-4">
          Back to Forms
        </Button>
      </div>
    );
  }

  const formData = form.data;

  const getLanguageBadgeVariant = (language) => {
    switch (language) {
      case 'en':
        return 'primary';
      case 'mr':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/forms')}
            className="flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{formData.title}</h1>
            <p className="text-gray-600 dark:text-gray-400">Form Details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleDownload} className="flex items-center gap-2">
            <Download size={16} />
            Download
          </Button>
          {hasCapability('canUploadForms') && (
            <Button variant="outline" onClick={handleEdit} className="flex items-center gap-2">
              <Edit size={16} />
              Edit
            </Button>
          )}
          {hasCapability('canDeleteForms') && (
            <Button variant="outline" onClick={handleDelete} className="flex items-center gap-2 text-red-600">
              <Trash2 size={16} />
              Delete
            </Button>
          )}
          {isAdmin() && (
            <Button
              variant="outline"
              onClick={() => setShowStats(!showStats)}
              className="flex items-center gap-2"
            >
              <BarChart3 size={16} />
              Stats
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Form Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Title</label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">{formData.title}</p>
                  </div>
                  {(formData.categoryInfo || formData.category) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Category</label>
                      {formData.categoryInfo ? (
                        <div className="text-sm text-gray-900 dark:text-white mt-1">
                          <span>{formData.categoryInfo.categoryName}</span>
                          {formData.categoryInfo.subcategoryName && (
                            <span className="text-gray-500"> {formData.categoryInfo.subcategoryName}</span>
                          )}
                          {formData.categoryInfo.subSubcategoryName && (
                            <span className="text-gray-500"> {formData.categoryInfo.subSubcategoryName}</span>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-900 dark:text-white mt-1">{formData.category}</p>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Language</label>
                    <div className="mt-1">
                      <Badge variant={getLanguageBadgeVariant(formData.language)}>
                        {formData.language === 'en' ? 'English' : 'Marathi'}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Visibility</label>
                    <div className="mt-1">
                      <Badge variant={formData.is_public ? 'success' : 'warning'}>
                        {formData.is_public ? 'Public' : 'Private'}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">File</label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">{formData.file_path}</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Form Preview */}
          <Card>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Form Preview</h3>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Form preview is not available. Click download to view the file.
                </p>
                <Button onClick={handleDownload} className="flex items-center gap-2 mx-auto">
                  <Download size={16} />
                  Download Form
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Upload Info */}
          <Card>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Upload Information</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Uploaded</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{formatDate(formData.created_at)}</p>
                  </div>
                </div>
                {formData.uploaded_by && (
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Uploaded By</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">User ID: {formData.uploaded_by}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Admin Statistics */}
          {isAdmin() && showStats && stats?.data && (
            <Card>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Statistics</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Eye className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Total Downloads</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {stats.data.stats?.totalDownloads || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default FormDetailPage;
